import { DEFAULT_GAME_MODE, presentationPhase, type GameModeId } from '../game/content/modes';
import Phaser from 'phaser';
import { PhaseBgm } from './PhaseBgm';
import { bgmCue } from './bgmCue';
import { GachaAudioDirector } from './GachaAudioDirector';
import { EvolutionSoundPlayer, type RewardSoundKey } from './EvolutionSoundPlayer';
import { evolutionKey } from './evolutionCues';
import { phaseTransitionKey } from './phaseCues';
import { runtimeBus } from '../app/runtimeBus';
import type { SessionState } from '../game/simulation/types';
import type { ResonancePhase } from '../game/simulation/dropBag';
import type { AudioAssetKey } from '../game/assets/manifest';
import type { SessionEvent } from '../game/simulation/types';

export type CollisionImpact = Readonly<{
  relativeSpeed: number;
  combinedMass: number;
}>;

const SOUND_EFFECT_VOLUME_SCALE = 0.8;

export class AudioDirector {
  private unlocked = false;
  private muted = false;
  private volume = 0.72;
  private lastCollisionAt = Number.NEGATIVE_INFINITY;
  private lastDangerAt = Number.NEGATIVE_INFINITY;
  private readonly lastPlayedAt = new Map<AudioAssetKey, number>();
  private collisionIndex = 0;
  private bgm: PhaseBgm | null = null;
  private observerVideo: HTMLVideoElement | null = null;
  private readonly gacha: GachaAudioDirector;
  private readonly evolution: EvolutionSoundPlayer | null;

  public syncBgm(state: SessionState): void {
    if (!this.unlocked) return;
    const manager = this.scene.sound;
    if (!this.bgm && manager instanceof Phaser.Sound.WebAudioSoundManager) {
      this.bgm = new PhaseBgm(manager.context, manager.destination, (key) => this.scene.cache.audio.get(key));
      this.bgm.setVolume(this.muted || this.observerVideo ? 0 : this.volume);
    }
    this.bgm?.sync(bgmCue(state),
      state.status === 'playing' || state.status === 'presenting');
  }

  public destroy(): void {
    this.evolution?.destroy();
    this.observerVideo = null;
    this.bgm?.destroy(); this.bgm = null; this.gacha.destroy();
    if (runtimeBus.gachaAudio === this.gacha) runtimeBus.gachaAudio = null;
  }

  public constructor(private readonly scene: Phaser.Scene) {
    const manager = scene.sound;
    const webAudio = manager instanceof Phaser.Sound.WebAudioSoundManager ? manager : null;
    this.gacha = new GachaAudioDirector(webAudio?.context ?? null, webAudio?.destination ?? null);
    this.evolution = webAudio ? new EvolutionSoundPlayer(webAudio.context, webAudio.destination,
      (key) => this.scene.cache.audio.get(key)) : null;
    this.evolution?.setVolume(this.effectsVolume, this.muted);
    this.gacha.setVolume(this.effectsVolume, this.muted);
    runtimeBus.gachaAudio = this.gacha;
  }

  public unlock(): void {
    if (this.unlocked) return;
    const manager = this.scene.sound;
    if (manager instanceof Phaser.Sound.WebAudioSoundManager) manager.unlock();
    this.unlocked = true;
  }

  public toggleMute(): void {
    this.muted = !this.muted;
    this.scene.sound.mute = this.muted;
    this.syncObserverMix();
    this.gacha.setVolume(this.effectsVolume, this.muted);
  }

  public setVolume(value: number): void {
    this.volume = Phaser.Math.Clamp(value, 0, 1);
    this.syncObserverMix();
    this.gacha.setVolume(this.effectsVolume, this.muted);
  }

  public setObserverVideo(video: HTMLVideoElement | null): void {
    this.observerVideo = video;
    this.syncObserverMix();
  }

  private get effectsVolume(): number {
    return this.volume * SOUND_EFFECT_VOLUME_SCALE;
  }

  private syncObserverMix(): void {
    this.evolution?.setVolume(this.effectsVolume, this.muted);
    this.bgm?.setVolume(this.muted || this.observerVideo ? 0 : this.volume);
    if (this.observerVideo) {
      this.observerVideo.volume = this.volume;
      this.observerVideo.muted = this.muted || this.volume === 0;
    }
  }

  public play(key: AudioAssetKey, volumeScale = 1, rate = 1): void {
    if (!this.unlocked || this.muted || !this.scene.cache.audio.exists(key)) return;
    // Physics can emit several identical rewards in one step. Keep their attacks
    // distinct instead of stacking identical waveforms and multiplying loudness.
    const now = this.scene.time.now;
    const spacing = key === 'button' ? 35 : key === 'chain-rise' ? 280 : 65;
    if (now - (this.lastPlayedAt.get(key) ?? Number.NEGATIVE_INFINITY) < spacing) return;
    this.lastPlayedAt.set(key, now);
    this.scene.sound.play(key, {
      volume: Phaser.Math.Clamp(this.volume * volumeScale, 0, 1) * SOUND_EFFECT_VOLUME_SCALE,
      rate,
    });
  }

  public handleEvents(events: readonly SessionEvent[], phase: ResonancePhase = 1, mode: GameModeId = DEFAULT_GAME_MODE): void {
    const merges = events.filter((event) => event.type === 'merge');
    const phaseChange = events.find((event): event is Extract<SessionEvent, { type: 'phase-changed' }> =>
      event.type === 'phase-changed' && event.phase > 1);
    // A transition-triggering merge belongs to the phase that just ended.
    // All merges in one physics step share one cue, without chain accents.
    if (merges.length > 0) {
      const mergePhase = presentationPhase(mode, phaseChange ? (phaseChange.phase - 1) as ResonancePhase : phase);
      this.playReward(evolutionKey(mergePhase), phaseChange ? 0.44 : 0.82);
    }
    for (const event of events) {
      if (event.type === 'drop-created') {
        this.play('drop-confirm', 0.65);
      } else if (event.type === 'phase-changed') {
        const key = phaseTransitionKey(presentationPhase(mode, event.phase));
        if (key) this.playReward(key, 0.86);
      } else if (event.type === 'card-use' && event.success) {
        this.play('card-ui', 0.36);
      } else if (event.type === 'presentation-requested') {
        if (event.presentation.kind === 'shinra' && !merges.some((merge) => merge.entityId === event.presentation.entityId)) {
          this.play('shinra-generation', 0.94);
        }
      } else if (event.type === 'game-over') {
        this.play('game-over', 0.9);
      }
    }
  }

  private playReward(key: RewardSoundKey, volume: number, delay = 0): boolean {
    if (!this.unlocked || this.muted || this.volume === 0) return false;
    if (this.evolution?.play(key, volume, delay)) return true;
    // Phaser's HTMLAudio fallback uses the same authored files and settings.
    if (!this.scene.cache.audio.exists(key)) return false;
    this.play(key, volume);
    return true;
  }

  public collision(impact: CollisionImpact, nowMs: number): void {
    if (nowMs - this.lastCollisionAt < 105 || impact.relativeSpeed < 0.55) return;
    this.lastCollisionAt = nowMs;
    const weight = impact.relativeSpeed * Math.sqrt(Math.max(0.1, impact.combinedMass));
    const strength = Phaser.Math.Clamp(weight / 7, 0.2, 1);
    // Small deterministic changes avoid a machine-gun texture on repeated hits.
    const rate = [1, 0.97, 1.025, 0.985][this.collisionIndex++ % 4]!;
    if (weight >= 3.4) this.play('contact-heavy', 0.34 + strength * 0.24, rate);
    else this.play('contact-light', 0.20 + strength * 0.18, rate);
  }

  public danger(active: boolean, nowMs: number): void {
    // Let the warning and its decay finish before repeating it.
    if (!active || nowMs - this.lastDangerAt < 1400) return;
    this.lastDangerAt = nowMs;
    this.play('danger-warning', 0.62);
  }
}
